import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { analytics } from "MVVM/Analytics";
import { UserSearchViewModel } from "./ViewModel";
import { ISearchItem, SearchBar } from "../SearchBar";

interface IUser {
  _id: string;
  id: string;
  avatar: string;
  avatarUrl: string;
  username: string;
  discord: {
    id: string;
  };
}

const UserSearch = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const history = useHistory();
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { userSearchQuery, loading } = UserSearchViewModel();

  const transformUserToSearchItem = (user: IUser): ISearchItem => ({
    id: user.id,
    name: user.username,
    image: user.avatarUrl || user.avatar,
  });

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.trim() !== "") {
        try {
          const response = await userSearchQuery({
            variables: {
              count: 10,
              query,
            },
          });
          setUsers(response.data.userSearch);
        } catch (_) {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    },
    [userSearchQuery, setUsers],
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debouncedSearchTimeoutRef.current)
        clearTimeout(debouncedSearchTimeoutRef.current);

      debouncedSearchTimeoutRef.current = setTimeout(() => {
        searchUsers(query);
      }, 300);
    },
    [searchUsers],
  );

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const onSelect = (item: ISearchItem) => {
    analytics.track("Button - Click", {
      location: "userSearch",
      locationIdentifier: "userSearchProfile",
      source: "website",
    });
    history.push(`/u/${item.name}`);
  };

  const onFocus = () => {
    analytics.track("Search - Focused", {
      location: "user_search",
      locationIdentifier: "userSearch",
    });
  };

  return (
    <>
      <SearchBar
        items={users.map(transformUserToSearchItem)}
        loading={loading}
        onSelect={onSelect}
        onFocus={onFocus}
        debouncedSearch={debouncedSearch}
        placeholder="Search for an Allstar Player..."
      />
    </>
  );
};

export { UserSearch };
